import { makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'
import { Api } from '@evertel/api'
import { APIDataRoomMessage } from '@evertel/types'
import { debounce, uniqBy } from 'lodash'
import { SessionService } from '@evertel/session/SessionService'
import { UnreadCountsState } from '@evertel/blue-user'

// Assuming MESSAGE_DEFAULT_INCLUDE is imported from the appropriate location
import { MESSAGE_DEFAULT_INCLUDE } from './MessageWallController'

import debugModule from 'debug'
const debug = debugModule('app:RepliesViewController')

class RepliesViewController {
    replies: APIDataRoomMessage[] = []
    repliesLatestFetch: APIDataRoomMessage[] = []
    roomId = 0
    searchTerm = ''
    page = 1
    limit = 10
    repliesCount = 0
    isInfiniteScroll = true
    isLoading = false
    isMarkingUnreads = false

    constructor(
        private api: Api,
        private session: SessionService,
        private unreadState: UnreadCountsState
    ) {
        makeAutoObservable(this)
    }

    init(roomId: number, isInfiniteScroll = true) {
        this.roomId = roomId
        this.isInfiniteScroll = isInfiniteScroll
        this.resetState()
    }

    setSearchTerm(value: string) {
        this.resetSearchMeta()
        this.searchTerm = value
    }

    setPage(page: number) {
        this.page = page
    }

    private _debouncedSearch = debounce(async () => this.search(), 300)

    debouncedSearch = () => {
        runInAction(() => {
            this.isLoading = true
        })
        this._debouncedSearch()
    }

    search = async () => {
        if (!this.roomId) return

        runInAction(() => {
            this.isLoading = true
        })

        let count = this.repliesCount
        let results: APIDataRoomMessage[] = []

        if (!this.repliesCount) {
            count = await this.fetchRepliesCount()
        }

        runInAction(() => {
            this.repliesLatestFetch = []
        })

        if (count > this.replies?.length) {
            try {
                results = await this.api.Routes.Room.getMessages(this.roomId, this.filter)
            } catch (e) {
                console.error(e)
            }
        }

        const processedResults = results.map(message => {
            message.meta = message.meta || {}

            if (message.repliesSubscriptions && message.repliesSubscriptions.length > 0) {
                const subscription = message.repliesSubscriptions.find(sub => sub.notify !== undefined)
                if (subscription) {
                    message.meta.repliesSubscription = { notify: subscription.notify }
                } else {
                    // No subscription found, treat it as a null to follow room settings
                    message.meta.repliesSubscription = { notify: null }
                }
            } else {
                // No repliesSubscriptions array, treat it as a null to follow room settings
                message.meta.repliesSubscription = { notify: null }
            }
            if (message.isRetracted) {
                message.type = 'retracted'
                message.ownerId = 1
            }

            return message
        })

        const apiDataResults = (this.isInfiniteScroll) ? uniqBy([...this.replies, ...processedResults], 'id') : processedResults

        runInAction(() => {
            this.repliesLatestFetch = processedResults
            this.replies = apiDataResults
            this.isLoading = false
        })
    }

    fetchNextReplies() {
        this.page = this.page + 1
        this.search()
    }

    async fetchRepliesCount() {
        const res = await this.api.Routes.Room.getMessagesCount(this.roomId, this.filter.where)

        runInAction(() => {
            this.repliesCount = res?.count ?? 0
        })

        return res?.count ?? 0
    }

    resetSearchMeta() {
        this.page = 1
        this.replies = []
        this.repliesCount = 0
    }

    resetState() {
        this.resetSearchMeta()
        this.searchTerm = ''
    }

    async markAllRepliesAsRead(roomId: number = null) {
        if (this.isMarkingUnreads) {
            return
        }

        const markRoomId = roomId ?? this.roomId

        try {
            runInAction(() => {
                this.isMarkingUnreads = true
            })

            await this.unreadState.markAllRepliesAsRead(markRoomId)
        } catch (error) {
            debug('markAllRepliesAsRead')

        } finally {
            runInAction(() => {
                this.isMarkingUnreads = false
            })
        }
    }

    get filter() {
        const f: any = {
            where: {
                hasReplies: true
            },
            order: 'updatedDate DESC',
            limit: this.limit,
            skip: (this.page - 1) * this.limit,
            include: [
                ...MESSAGE_DEFAULT_INCLUDE,
                {
                    relation: 'repliesSubscriptions',
                    scope: {
                        where: {
                            blueUserId: this.session.currentUserId
                        },
                        fields: ['id', 'notify']
                    }
                }
            ]
        }

        if (this.searchTerm !== '') {
            f.where.searchTerm = this.searchTerm
        }

        return f
    }
}

decorate(injectable(), RepliesViewController)
decorate(inject(Api), RepliesViewController, 0)
decorate(inject(SessionService), RepliesViewController, 1)
decorate(inject(UnreadCountsState), RepliesViewController, 2)

export { RepliesViewController }
